import type {
  ListingIssue,
  SkuProductTaxonomy,
  TaxonomyCategory,
  TaxonomyConfidence,
  TaxonomySource,
} from '../api/client.js';
import { gaugeLabel, productsPath, putUpWithLength } from './products.js';

/**
 * What the SKU page's Product card shows: the resolved taxonomy of the SKU's
 * product as label/value facts, and the links to open it in the Products browser
 * or, when it still needs a decision, in the review queue.
 */

/** One product's category, in the singular (the browser's cards use the plural). */
export const CATEGORY_NAME: Record<TaxonomyCategory, string> = {
  racquet: 'Racquet',
  string: 'String',
  grip: 'Grip',
  footwear: 'Footwear',
  bag: 'Bag',
  other: 'Other',
};

/** Where an assignment came from, as the review page names it. */
export const SOURCE_NAME: Record<TaxonomySource, string> = {
  override: 'Owner',
  booking: 'Booking',
  heuristic: 'Title',
  catalog: 'Catalog',
};

export const CONFIDENCE_NAME: Record<TaxonomyConfidence, string> = {
  exact: 'Exact',
  likely: 'Likely',
  weak: 'Weak',
  none: 'Unassigned',
};

export interface TaxonomyFact {
  label: string;
  value: string;
}

/** A size label with its canonical form when that says something more: "Grip 2 (4 1/4)". */
function sizeText(label: string | null, canonical: string | null): string {
  if (label === null) return canonical ?? '—';
  return canonical && canonical !== label ? `${label} (${canonical})` : label;
}

/**
 * The card's facts in order: category, series, model, color, then the size — or,
 * for a string, its gauge and put-up in place of a size — then where it came from.
 */
export function taxonomyFacts(t: SkuProductTaxonomy): TaxonomyFact[] {
  const facts: TaxonomyFact[] = [
    { label: 'Category', value: t.category ? CATEGORY_NAME[t.category] : 'Unassigned' },
    { label: 'Series', value: t.series ?? '—' },
    { label: 'Model', value: t.model ?? '—' },
    { label: 'Color', value: t.color ?? '—' },
  ];
  if (t.category === 'string') {
    facts.push({ label: 'Gauge', value: gaugeLabel(t.gauge, t.gaugeMm) });
    facts.push({ label: 'Put-up', value: putUpWithLength(t.putUp, t.putUpLength) });
  } else {
    facts.push({ label: 'Size', value: sizeText(t.sizeLabel, t.sizeCanonical) });
  }
  facts.push({
    label: 'Source',
    value:
      t.source === null
        ? 'Not resolved yet'
        : `${SOURCE_NAME[t.source]}, ${CONFIDENCE_NAME[t.confidence].toLowerCase()}`,
  });
  return facts;
}

/** A listing issue's short name, as the review page labels it. */
export const LISTING_ISSUE_NAME: Record<ListingIssue, string> = {
  type_conflict: 'Product type conflict',
  type_unmapped: 'Unmapped product type',
};

/**
 * The listing issue in plain words — its name and the review card's own sentence
 * ("Amazon's type FISHING_REEL is not one the app maps; resolved as string …") —
 * or null when Amazon's product type agrees. An override row can carry one too.
 */
export function listingIssueText(t: SkuProductTaxonomy): string | null {
  if (t.listingIssue === null) return null;
  const name = LISTING_ISSUE_NAME[t.listingIssue];
  return t.listingIssueReason ? `${name}: ${t.listingIssueReason}` : name;
}

/**
 * The Products browser at this product, and the review queue when it is in it —
 * filtered to the listing issues when the product carries one, so the link lands
 * on the card that needs the decision, and including the retired products when
 * this one is retired (the queue hides those by default).
 */
export function taxonomyLinks(t: SkuProductTaxonomy): { products: string; review: string | null } {
  const products = productsPath(t.drill);
  if (!t.inReview && t.listingIssue === null) return { products, review: null };
  const params = new URLSearchParams();
  if (t.listingIssue !== null) params.set('filter', 'listing-issues');
  if (t.retired) params.set('include', 'retired');
  const query = params.toString();
  return { products, review: query ? `/products/review?${query}` : '/products/review' };
}

/**
 * The note for a retired product still needing review: none of its SKUs is Active,
 * so the review queue hides it unless retired products are included. Null for a
 * live product, or one with nothing to review.
 */
export function retiredNote(t: SkuProductTaxonomy): string | null {
  if (!t.retired || (!t.inReview && t.listingIssue === null)) return null;
  return 'Retired: none of its SKUs is Active, so the review queue hides it by default; the link includes retired products.';
}
